import { useMemo } from 'react'
import styles from './styles.module.scss'
import CartItem from '@components/cart/shared/CartProductsList/CartItem'
import { CustomOrder, OrderItem } from '../../helpers/transformOrder'
import currencyConverter from '@helpers/misc/currencyConverter'
import {
  DiscountAllocation,
  DiscountAllocationTargetSelection
} from '@components/account/types'
import DiscountCode from '@components/cart/CartSideDrawerOverlay/DiscountCode'
import PriceComparison from '@components/ui/PriceComparison'
import OrderPageContentSummary from './OrderPageContentSummary'
import OrderPageContentHeaderInfo from './OrderPageContentHeaderInfo'
import { Button } from '@components/ui/Button'
import { useRouter } from 'next/router'
import { getLink } from '@helpers/env'
import trackError from '@helpers/trackError'
import { getProductById } from '@api/product'
import { decodeProductId } from '@helpers/shopify'

function Discounts({
  discounts,
  totalDiscount
}: {
  discounts: DiscountAllocation[] | null
  totalDiscount?: string
}) {
  if (
    !discounts?.some(
      discount => discount.discountApplication.targetSelection !== 'ALL'
    )
  ) {
    return null
  }

  return (
    <>
      {discounts.map((discount, index) => (
        <div
          key={`${totalDiscount}-${index}`}
          className={styles.OrderPageItem__discount}
        >
          <DiscountCode
            code={`${
              discount.discountApplication?.code ||
              discount.discountApplication?.title
            } ${
              totalDiscount
                ? `(-${currencyConverter({ price: totalDiscount })})`
                : ''
            }`}
            variant="small"
          />
        </div>
      ))}
    </>
  )
}

function isDiscountByAllocationTarget(
  discountAllocations: DiscountAllocation[] | null,
  selection: DiscountAllocationTargetSelection
) {
  return (
    discountAllocations?.some(
      discount => discount.discountApplication.targetSelection === selection
    ) ?? false
  )
}

function OrderPageItem({
  createdAt,
  item,
  isFulfilled
}: {
  createdAt: string
  item: OrderItem
  isFulfilled: boolean
}) {
  const router = useRouter()

  const hasLineItemDiscount = useMemo(
    () => isDiscountByAllocationTarget(item.discountAllocations, 'ENTITLED'),
    [item.discountAllocations]
  )

  const handleRedirectToProductPage = async () => {
    try {
      const productIdDecoded = decodeProductId(item.productId)
      const productData = await getProductById({ id: productIdDecoded })
      const handle = productData?.productById?.handle

      if (!handle) {
        throw new Error(`No product handle found for id: ${productIdDecoded}`)
      }

      router.push(getLink(`/products/${handle}`), undefined, { shallow: true })
    } catch (error) {
      trackError(new Error('getProductById failed'), {
        error: error as Error,
        productId: item.productId
      })
    }
  }

  return (
    <div className={styles.OrderPageItem}>
      <CartItem
        image={item?.image?.url}
        productTitle={item.title}
        productType={item.productType}
        sku={item.sku ?? undefined}
        properties={item.customAttributes}
        layout="small"
        handleOnClick={handleRedirectToProductPage}
        createdAt={createdAt}
      >
        <p className={styles.OrderPageItem__quantity}>
          Quantity: {item.quantity}
        </p>
        <div className={styles.OrderPageItem__pricesWrapper}>
          <PriceComparison
            classes={styles.OrderPageItem__price}
            price={item.currentTotalPrice}
            fullPrice={Number(item.price) * item.quantity}
            size="medium-small-bold"
          />
        </div>
        {hasLineItemDiscount && (
          <Discounts
            discounts={item.discountAllocations}
            totalDiscount={item.totalDiscount}
          />
        )}
      </CartItem>
      {item.refundableQuantity === 0 ? (
        <p className={styles.OrderPageItem__returned}>
          This item has been returned.
        </p>
      ) : !isFulfilled ? (
        <p className={styles.OrderPageItem__pending}>
          This item is in preparation.
        </p>
      ) : (
        <Button
          variant="secondary"
          classes={styles.reoderButton}
          onClick={handleRedirectToProductPage}
        >
          Reorder
        </Button>
      )}
    </div>
  )
}

export function OrderPageContent({
  order,
  orderSummary,
  status
}: {
  status: string
  order: CustomOrder
  orderSummary: string
}) {
  const fulfilledVariantIds = useMemo(() => {
    const ids = new Set<string>()
    order.fulfillments?.forEach(fulfillment => {
      fulfillment.fulfillmentLineItems.forEach(({ lineItem }) => {
        if (lineItem.variantId) ids.add(lineItem.variantId)
      })
    })
    return ids
  }, [order.fulfillments])

  return (
    <div className={styles.OrderPageContent}>
      <OrderPageContentHeaderInfo
        order={order}
        orderSummary={orderSummary}
        status={status}
      />

      {order.orderItems.map(item => (
        <OrderPageItem
          createdAt={order.createdAt}
          item={item}
          isFulfilled={fulfilledVariantIds.has(item.variantId ?? '')}
          key={item.productId}
        />
      ))}

      <OrderPageContentSummary order={order} />
    </div>
  )
}
